/**
 * The filter chips used across Ståtid's filter controls.
 *
 * Two things this fixes over the per-file `Chip` copies it replaces:
 *
 * 1. A selected chip used to be solid `bg-primary-600` — the exact weight of
 *    the Oppdater button that applies it. The filter out-shouted the action.
 *    Selection is now tonal: same geometry, cyan border and tint. Still
 *    unmistakable, no longer competing with the primary action.
 * 2. An unselected chip has a border too, so selecting one changes its colour
 *    rather than making a box appear. No reflow, and the difference reads as
 *    state rather than as two different kinds of control.
 *
 * Selection is carried by border, background AND weight, never colour alone,
 * and `aria-pressed` states it outright for anyone not reading pixels.
 */

import { useTranslation } from 'react-i18next'

const BASE = 'px-2.5 py-1 rounded-sm border text-xs transition-colors'

const SELECTED =
  'border-primary-600 bg-primary-50 text-primary-700 font-medium ' +
  'dark:border-primary-400 dark:bg-primary-900/40 dark:text-primary-200'

const UNSELECTED =
  'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-400 ' +
  'dark:border-dark-border dark:bg-dark-card dark:text-gray-400 dark:hover:bg-dark-border'

interface ChipProps {
  active: boolean
  label: string
  onClick: () => void
  /** Set when the visible label alone is ambiguous — see ChipGroup's "Alle". */
  ariaLabel?: string
}

export function FilterChip({ active, label, onClick, ariaLabel }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      className={`${BASE} ${active ? SELECTED : UNSELECTED}`}
    >
      {label}
    </button>
  )
}

interface ChipGroupProps<T extends string | number> {
  /** Group heading, e.g. "Innkjøpstype". Also disambiguates the Alle chip. */
  label: string
  options: readonly { value: T; label: string }[]
  selected: readonly T[]
  onChange: (next: T[]) => void
}

/**
 * A labelled chip group with an explicit "Alle".
 *
 * An empty selection means "no filter — every value included", but with only
 * value chips on screen that state is indistinguishable from "I haven't
 * chosen yet", and in a filter box where nothing applies until Oppdater that
 * ambiguity is expensive. The Alle chip makes the resting state a visible,
 * selected choice, and clicking it is how you get back to it — the same
 * affordance the Eierskifte control has always had.
 */
export function ChipGroup<T extends string | number>({
  label, options, selected, onChange,
}: ChipGroupProps<T>) {
  const { t } = useTranslation('staatid')
  const alle = selected.length === 0

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        <FilterChip
          active={alle}
          label={t('common.alle')}
          // Three groups each render an "Alle"; the heading is what tells them
          // apart, so it belongs in the accessible name.
          ariaLabel={t('common.alleGroup', { label })}
          onClick={() => onChange([])}
        />
        {options.map(o => (
          <FilterChip
            key={o.value}
            active={selected.includes(o.value)}
            label={o.label}
            onClick={() =>
              onChange(
                selected.includes(o.value)
                  ? selected.filter(v => v !== o.value)
                  : [...selected, o.value],
              )
            }
          />
        ))}
      </div>
    </div>
  )
}
